/**
 *
 * @format
 */

/** @format */
import React, { useState } from 'react';
import './styles.scss';
import { Col, Row, Button, Alert } from 'react-bootstrap';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { regions } from '../../../views/Onboarding/Onboarding/ActiveAreas/regions';
import { createLeadApiService } from 'services/apiService';
import ButtonSyncLoader from 'components/ButtonSyncLoader';

import { PropertyTypes } from 'utils/propertyObjectHelper';
import { Selector } from '../components';
import {
  LeadTypes,
  FormSubmitStates,
  leadBrokerStatuses,
  leadUrgency
} from '../constants';

const ImportantFieldMark = () => <span style={{ color: 'red' }}>*</span>;

const TOTAL_FORM_STAGES = 3;

export const LeadsSectionMultiStageBody = (props) => {
  const text = props.t('lang_common:leads_generator');
  const propertyTypeNames = props.t('lang_common:object_listing:propertyType');

  const {
    showPropertyTypes = false,
    showUrgency = false,
    showTextInputs = true,
    showLeadTypes = true,
    showSquareMeterInput = false,
    showNumberOfRoomsInput = false,
    showAddressInput = false,
    showBrokerStatusSelector = false,
    showTelephoneInput = false
  } = props || {};

  const Messages = {
    PLEASE_SELECT_TYPE: text.form.message.select_type.text,
    PLEASE_TYPE_NAME: text.form.message.type_name.text,
    PLEASE_TYPE_EMAIL: text.form.message.type_email.text,
    EMAIL_IS_NOT_VALID: text.form.message.email_not_valid.text,
    PLEASE_SELECT_CITY: text.form.message.select_city.text,
    PLEASE_TYPE_SQ_METERS: text.form.message.sq_meters.text,
    SQ_METERS_SHOULD_BE_NUMBER:
      text.form.message.sq_meters_should_be_number.text,
    PLEASE_TYPE_NUMBER_OF_ROOMS: text.form.message.number_of_rooms.text,
    NUMBER_OF_ROOMS_SHOULD_BE_NUMBER:
      text.form.message.number_of_rooms_should_be_number.text,
    SUCCESSFULLY_SENT: text.form.message.sent_success.text,
    ERROR_OCCURRED: text.form.message.sent_failed.text
  };

  const [leadType, setLeadType] = useState(null);
  const [citySelectKey, setCitySelectKey] = useState(0);

  const [formSubmitState, setFormSubmitState] = useState(
    FormSubmitStates.INITIAL
  );

  const [propertyTypes, setPropertyTypes] = useState([]);
  const [urgency, setUrgency] = useState(null);
  const [leadBrokerStatus, setLeadBrokerStatus] = useState(null);
  const [leadFormStage, setLeadFormStage] = useState(1);

  const moveToNextStage = () => {
    if (leadFormStage < TOTAL_FORM_STAGES) {
      setLeadFormStage(leadFormStage + 1);
    }
  };

  const moveToPreviousStage = () => {
    if (leadFormStage > 1) {
      setLeadFormStage(leadFormStage - 1);
    }
  };

  const leadsFormValidationSchema = () => {
    const validationProps = {
      leadType: Yup.string().required(Messages.PLEASE_SELECT_TYPE),
      name: Yup.string().required(Messages.PLEASE_TYPE_NAME),
      email: Yup.string()
        .email(Messages.EMAIL_IS_NOT_VALID)
        .required(Messages.PLEASE_TYPE_EMAIL),
      city: Yup.string().required(Messages.PLEASE_SELECT_CITY)
    };

    if (showSquareMeterInput) {
      validationProps.sqMeters = Yup.number()
        .typeError(Messages.SQ_METERS_SHOULD_BE_NUMBER)
        .required(Messages.PLEASE_TYPE_SQ_METERS)
        .positive(Messages.SQ_METERS_SHOULD_BE_NUMBER);
    }

    if (showNumberOfRoomsInput) {
      validationProps.numberOfRooms = Yup.number()
        .typeError(Messages.NUMBER_OF_ROOMS_SHOULD_BE_NUMBER)
        .required(Messages.PLEASE_TYPE_NUMBER_OF_ROOMS)
        .positive(Messages.NUMBER_OF_ROOMS_SHOULD_BE_NUMBER)
        .integer(Messages.NUMBER_OF_ROOMS_SHOULD_BE_NUMBER);
    }

    return Yup.object().shape(validationProps);
  };

  const formik = useFormik({
    initialValues: {
      leadType: leadType || '',
      name: '',
      email: '',
      city: '',
      details: '',
      sqMeters: '',
      numberOfRooms: '',
      address: '',
      broker_status: '',
      phone: ''
    },
    onSubmit: (values, { resetForm }) => {
      const checkIsReady = () => {
        return leadType && values.name && values.email && values.city;
      };

      const clearLeadsForm = () => {
        resetForm();
        setLeadType(null);
        setCitySelectKey(citySelectKey + 1);
      };

      if (checkIsReady()) {
        setFormSubmitState(FormSubmitStates.PENDING);
        createLeadApiService({
          leadType,
          name: values.name,
          email: values.email,
          location: values.city,
          details: values.details,
          source: props.metadata?.source || undefined,
          brokerId: props.metadata?.brokerId || undefined,
          objectId: props.metadata?.objectId || undefined
        })
          .then((response) => {
            if (response?.data?.status === 'success') {
              clearLeadsForm();
              setFormSubmitState(FormSubmitStates.SUCCESS);
              setTimeout(
                () => setFormSubmitState(FormSubmitStates.INITIAL),
                5000
              );
            } else {
              setFormSubmitState(FormSubmitStates.FAIL);
            }
          })
          .catch(() => {
            setFormSubmitState(FormSubmitStates.FAIL);
          });
      }
    },
    validationSchema: leadsFormValidationSchema,
    validateOnChange: true,
    validateOnBlur: false
  });

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const leadTypeSelectHandler = (type) => {
    setLeadType(type);
    formik.setFieldValue('leadType', type, true);
  };

  const leadTypeIndicator = (type) => {
    if (type === leadType) {
      return 'active';
    }

    return '';
  };

  const propertyTypeSelectHandler = (type) => {
    if (!propertyTypes.includes(type)) {
      setPropertyTypes([...propertyTypes, type]);
    } else {
      setPropertyTypes(propertyTypes.filter((t) => t !== type));
    }
    // formik.setFieldValue('propertyType', type, true);
  };

  const propertyTypeIndicator = (type) => {
    if (propertyTypes.includes(type)) {
      return 'active';
    }

    return '';
  };

  const urgencySelectHandler = (value) => {
    setUrgency(value);
    // formik.setFieldValue('leadType', type, true);
  };

  const urgencyIndicator = (value) => {
    if (value === urgency) {
      return 'active';
    }

    return '';
  };

  const brokerStatusSelectHandler = (value) => {
    setLeadBrokerStatus(value);
    // formik.setFieldValue('leadType', type, true);
  };

  const brokerStatusIndicator = (value) => {
    if (value === leadBrokerStatus) {
      return 'active';
    }

    return '';
  };

  const LeadSelector = ({ leadType, children }) => (
    <Selector
      wrapperClassName='lead-selector-button-wrapper'
      buttonClassName={`${leadTypeIndicator(leadType)}`}
      onClick={() => leadTypeSelectHandler(leadType)}>
      {children}
    </Selector>
  );
  const PropertyTypeSelector = ({ propertyType, children }) => (
    <Selector
      wrapperClassName='property-type-selector-button-wrapper'
      buttonClassName={`${propertyTypeIndicator(propertyType)}`}
      onClick={() => propertyTypeSelectHandler(propertyType)}>
      {children}
    </Selector>
  );

  const UrgencySelector = ({ urgency, children }) => (
    <Selector
      wrapperClassName='urgency-selector-button-wrapper'
      buttonClassName={`${urgencyIndicator(urgency)}`}
      onClick={() => urgencySelectHandler(urgency)}>
      {children}
    </Selector>
  );

  const LeadBrokerStatusSelector = ({ status, children }) => (
    <Selector
      wrapperClassName='broker-status-selector-button-wrapper'
      buttonClassName={`${brokerStatusIndicator(status)}`}
      onClick={() => brokerStatusSelectHandler(status)}>
      {children}
    </Selector>
  );

  const FormElementErrorSection = ({ formikError }) => (
    <label className='registerpage-description registerpage-validation-error-label'>
      {formikError != undefined && formikError}
    </label>
  );

  const FormSubmitAlert = () => {
    const displayAlert = () => {
      return (
        formSubmitState === FormSubmitStates.SUCCESS ||
        formSubmitState === FormSubmitStates.FAIL
      );
    };

    const { variant, message } = (() => {
      let response = {
        variant: null,
        message: ''
      };
      if (formSubmitState === FormSubmitStates.SUCCESS) {
        response.variant = 'success';
        response.message = Messages.SUCCESSFULLY_SENT;
      } else if (formSubmitState === FormSubmitStates.FAIL) {
        response.variant = 'danger';
        response.message = Messages.ERROR_OCCURRED;
      }
      return response;
    })();

    return (
      displayAlert() && (
        <Alert variant={variant}>
          <label className='text3m'>{message}</label>
        </Alert>
      )
    );
  };

  const LeadTypeSelectionRow = () => {
    if (showLeadTypes) {
      return (
        <Row>
          <Col className='registerpage-field-container'>
            <label className='text3m'>
              {text.form.select_lead_type.label} <ImportantFieldMark />
            </label>
            <div className='leads-selector-button-group'>
              <LeadSelector leadType={LeadTypes.SELL}>
                {text.form.lead_type.sell.label}
              </LeadSelector>
              <LeadSelector leadType={LeadTypes.BUY}>
                {text.form.lead_type.buy.label}
              </LeadSelector>
              <LeadSelector leadType={LeadTypes.FREE_VALUATION}>
                {text.form.lead_type.free_valuation.label}
              </LeadSelector>
              <LeadSelector leadType={LeadTypes.OTHER}>
                {text.form.lead_type.other.label}
              </LeadSelector>
            </div>
            <FormElementErrorSection formikError={formik.errors.leadType} />
          </Col>
        </Row>
      );
    }

    return <></>;
  };

  const PropertyTypesSelectionRow = () => {
    if (showPropertyTypes) {
      return (
        <Row>
          <Col className='registerpage-field-container'>
            <label className='text3m'>
              {text.form.select_property_type.label} <ImportantFieldMark />
            </label>
            <div className='property-type-selector-button-group'>
              {Object.keys(PropertyTypes).map((typeKey, index) => {
                if (
                  ![
                    PropertyTypes.Project,
                    PropertyTypes.Premises,
                    PropertyTypes.ForeignProperty,
                    PropertyTypes.CommercialProperty,
                    PropertyTypes.Condominium
                  ].includes(typeKey)
                ) {
                  return (
                    <PropertyTypeSelector
                      key={index}
                      propertyType={PropertyTypes[typeKey]}>
                      {propertyTypeNames[PropertyTypes[typeKey]]}
                    </PropertyTypeSelector>
                  );
                }
                return <></>;
              })}
            </div>
            <FormElementErrorSection formikError={formik.errors.leadType} />
          </Col>
        </Row>
      );
    }

    return <></>;
  };

  const UrgencySelectorRow = () => {
    if (showUrgency) {
      return (
        <Row>
          <Col className='registerpage-field-container'>
            <label className='text3m'>
              {text.form.select_urgency.label} <ImportantFieldMark />
            </label>
            <div className='urgency-selector-button-group'>
              <UrgencySelector urgency={leadUrgency.URGENT}>
                {text?.urgency?.urgent?.label}
              </UrgencySelector>
              <UrgencySelector urgency={leadUrgency.NOT_URGENT}>
                {text?.urgency?.not_urgent?.label}
              </UrgencySelector>
            </div>
            <FormElementErrorSection formikError={formik.errors.leadType} />
          </Col>
        </Row>
      );
    }

    return <></>;
  };

  const BrokerStatusSelectorRow = () => {
    if (showBrokerStatusSelector) {
      return (
        <Row>
          <Col className='registerpage-field-container'>
            {/* <label className='text3m'>
              {text.form.select_urgency.label} <ImportantFieldMark />
            </label> */}
            <div className='broker-status-selector-button-group'>
              <LeadBrokerStatusSelector status={leadBrokerStatuses.HAVE_BROKER}>
                {text?.broker_status?.have_broker?.text}
              </LeadBrokerStatusSelector>
              <LeadBrokerStatusSelector
                status={leadBrokerStatuses.WANT_BROKER_OFFERS}>
                {text?.broker_status?.want_broker_offers?.text}
              </LeadBrokerStatusSelector>
            </div>
            <FormElementErrorSection
              formikError={formik.errors.broker_status}
            />
          </Col>
        </Row>
      );
    }

    return <></>;
  };

  const HeaderSection = () => (
    <Row>
      <div className='header-section'>
        <span>Lokala experter hjälper dig</span>
        <p>
          Fyll i formuläret och få kontakt med anslutna mäklare. Gratis och
          ingen bindning.
        </p>
      </div>
    </Row>
  );

  const FirstStage = () => {
    if (leadFormStage === 1) {
      return (
        <div className='first-stage-body stage-body'>
          <BrokerStatusSelectorRow />
          <PropertyTypesSelectionRow />
          <LeadTypeSelectionRow />

          <Row>
            {showSquareMeterInput && (
              <Col className='registerpage-field-container'>
                <label htmlFor='sqMeters' className='text3m'>
                  {text.form.sqMeters.label} <ImportantFieldMark />
                </label>
                <input
                  id='sqMeters'
                  name='sqMeters'
                  type='text'
                  placeholder={text.placeholder}
                  className='registerpage-field-input'
                  onChange={formik.handleChange}
                  value={formik.values.sqMeters}
                />
                <FormElementErrorSection formikError={formik.errors.sqMeters} />
              </Col>
            )}
            {showNumberOfRoomsInput && (
              <Col className='registerpage-field-container'>
                <label htmlFor='numberOfRooms' className='text3m'>
                  {text.form.numberOfRooms.label} <ImportantFieldMark />
                </label>
                <input
                  id='numberOfRooms'
                  name='numberOfRooms'
                  type='text'
                  placeholder={text.placeholder}
                  className='registerpage-field-input'
                  onChange={formik.handleChange}
                  value={formik.values.numberOfRooms}
                />
                <FormElementErrorSection
                  formikError={formik.errors.numberOfRooms}
                />
              </Col>
            )}
          </Row>
        </div>
      );
    }
  };

  const SecondStage = () => {
    if (leadFormStage === 2) {
      return (
        <div className='second-stage-body stage-body'>
          <UrgencySelectorRow />
          {showAddressInput && (
            <Row>
              <Col className='registerpage-field-container'>
                <label htmlFor='address' className='text3m'>
                  {text.form.address.label} <ImportantFieldMark />
                </label>
                <input
                  id='address'
                  name='address'
                  type='text'
                  placeholder={text.placeholder}
                  className='registerpage-field-input'
                  onChange={formik.handleChange}
                  value={formik.values.address}
                />
                <FormElementErrorSection formikError={formik.errors.address} />
              </Col>
            </Row>
          )}

          <Row>
            <Col className='registerpage-field-container'>
              <label htmlFor='region' className='text3m'>
                {text.form.city.label} <ImportantFieldMark />
              </label>
              <select
                key={citySelectKey}
                id='city'
                name='city'
                className='registerpage-field-input text2r'
                defaultValue=''
                onChange={formik.handleChange}>
                <option key='' className='text2r' selected={true} value=''>
                  {text.form.city.default_option.label}
                </option>
                {regions.map((value, index) => (
                  <option className='text2r' value={value} key={index}>
                    {value}
                  </option>
                ))}
              </select>
              <FormElementErrorSection formikError={formik.errors.city} />
            </Col>
          </Row>

          <Row>
            <Col className='registerpage-field-container'>
              <label htmlFor='lastName' className='text3m'>
                {text.form.name.label} <ImportantFieldMark />
              </label>
              <input
                id='name'
                name='name'
                type='text'
                placeholder={text.form.name.placeholder}
                className='registerpage-field-input'
                onChange={formik.handleChange}
                value={formik.values.name}
              />
              <FormElementErrorSection formikError={formik.errors.name} />
            </Col>
          </Row>
        </div>
      );
    }
  };

  const ThirdStage = () => {
    if (leadFormStage === 3) {
      return (
        <Row>
          <Col sm={12} md={12}>
            <form>
              {showTextInputs && (
                <>
                  <Row>
                    <Col className='registerpage-field-container'>
                      <label htmlFor='email' className='text3m'>
                        {text.form.email.label} <ImportantFieldMark />
                      </label>
                      <input
                        id='email'
                        name='email'
                        type='text'
                        placeholder={text.placeholder}
                        className='registerpage-field-input'
                        onChange={formik.handleChange}
                        value={formik.values.email}
                      />
                      <FormElementErrorSection
                        formikError={formik.errors.email}
                      />
                    </Col>
                  </Row>
                  {showTelephoneInput && (
                    <Row>
                      <Col className='registerpage-field-container'>
                        <label htmlFor='email' className='text3m'>
                          {text.form.phone_number.label}
                        </label>
                        <input
                          id='phone'
                          name='phone'
                          type='text'
                          placeholder={text.placeholder}
                          className='registerpage-field-input'
                          onChange={formik.handleChange}
                          value={formik.values.phone}
                        />
                        <FormElementErrorSection
                          formikError={formik.errors.phone}
                        />
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col className='registerpage-field-container'>
                      <label htmlFor='description' className='text3m'>
                        {text.form.little_more.label}
                      </label>
                      <textarea
                        id='details'
                        name='details'
                        onChange={formik.handleChange}
                        value={formik.values.details}
                        placeholder={text.form.little_more.placeholder}
                        className='leads-additional-info-textarea'
                      />

                      <FormSubmitAlert />
                    </Col>
                  </Row>
                </>
              )}
              {/* <div
                className='registerpage-button-container'
                style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <Button
                  className='btn btn-pink leads-submit-button mobile-registerpage-button-width'
                  type='button'
                  disabled={formSubmitState === FormSubmitStates.PENDING}
                  onClick={handleSubmit}>
                  {formSubmitState === FormSubmitStates.PENDING ? (
                    <ButtonSyncLoader />
                  ) : (
                    text.form.submit_button.label
                  )}
                </Button>
              </div> */}
            </form>
          </Col>
        </Row>
      );
    }
  };

  const ButtonsSection = () => {
    const NextButton = () => {
      if (leadFormStage < TOTAL_FORM_STAGES) {
        return (
          <Button
            className='btn btn-pink leads-submit-button mobile-registerpage-button-width'
            type='button'
            onClick={moveToNextStage}>
            Next
          </Button>
        );
      }
    };
    const PreviousButton = () => {
      if (leadFormStage > 1) {
        return (
          <Button
            className='btn btn-white leads-submit-button mobile-registerpage-button-width'
            type='button'
            onClick={moveToPreviousStage}>
            Previous
          </Button>
        );
      }
    };
    const SubmitButton = () => {
      if (leadFormStage === TOTAL_FORM_STAGES) {
        return (
          <Button
            className='btn btn-pink leads-submit-button mobile-registerpage-button-width'
            type='button'
            disabled={formSubmitState === FormSubmitStates.PENDING}
            onClick={handleSubmit}>
            {formSubmitState === FormSubmitStates.PENDING ? (
              <ButtonSyncLoader />
            ) : (
              text.form.submit_button.label
            )}
          </Button>
        );
      }
    };

    return (
      <div className='leads-form-multi-stage-button-container'>
        <PreviousButton />
        <NextButton />
        <SubmitButton />
      </div>
    );
  };

  return (
    <div className='leads-generator-multi-stage-body' id='leads-form'>
      <HeaderSection />
      <FirstStage />
      <SecondStage />
      <ThirdStage />
      <ButtonsSection />
    </div>
  );
};
